import React, { useState } from 'react'
import * as JobFileApi from '../../api/jobfile.api'
import { openNotificationWithIcon } from '../../utils'
import { Pagination, Spin } from 'antd'
import {
  FileImageOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileOutlined,
  DownloadOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import mixpanel from 'mixpanel-browser'

const IMAGE_TYPES = ['.jpg', '.jpeg', '.png']
const PDF_TYPES = ['.pdf']
const EXCEL_TYPES = ['.xls', '.xlsx', '.xlsm', '.csv']
const WORD_TYPES = ['.doc']

function getExt(name = '') {
  return name.slice(name.lastIndexOf('.')).toLowerCase()
}

function getFileType(name) {
  const ext = getExt(name)
  if (IMAGE_TYPES.includes(ext)) return 'image'
  if (PDF_TYPES.includes(ext)) return 'pdf'
  if (EXCEL_TYPES.includes(ext)) return 'excel'
  if (WORD_TYPES.includes(ext)) return 'word'
  return 'other'
}

function FileIcon({ name }) {
  const type = getFileType(name)
  const icons = {
    image: <FileImageOutlined className="ft-file-icon ft-file-icon--image" />,
    pdf:   <FilePdfOutlined   className="ft-file-icon ft-file-icon--pdf"   />,
    excel: <FileExcelOutlined className="ft-file-icon ft-file-icon--excel" />,
    word:  <FileWordOutlined  className="ft-file-icon ft-file-icon--word"  />,
    other: <FileOutlined      className="ft-file-icon ft-file-icon--other" />,
  }
  return icons[type]
}

function FileTypeBadge({ name }) {
  const ext = getExt(name).replace('.', '').toUpperCase()
  const type = getFileType(name)
  return <span className={`ft-badge ft-badge--${type}`}>{ext}</span>
}

function shortName(name = '', max = 36) {
  if (name.length <= max) return name
  const ext = getExt(name)
  return `${name.slice(0, max - ext.length - 3)}…${ext}`
}

const FileTable = ({ user, files = [], job, setFiles, socket }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 7
  const [isDeleting, setIsDeleting] = useState(null)
  const [isDownloading, setIsDownloading] = useState(null)

  const sortedFiles = [...files].sort(
    (a, b) => new Date(b.jobFileData?.updatedAt) - new Date(a.jobFileData?.updatedAt)
  )
  const startIndex = (currentPage - 1) * pageSize
  const filesToShow = sortedFiles.slice(startIndex, startIndex + pageSize)

  const canDelete = (file) => {
    const { uploadedBy, job: fileJob } = file.jobFileData
    if (job.is_transferred && job.transfer_reference_job === fileJob) {
      return user.userType === 'customer'
    }
    return (
      !(uploadedBy === 'customer' && user.userType === 'technician') &&
      !(uploadedBy === 'technician' && user.userType === 'customer')
    )
  }

  const handleDelete = async (fileName) => {
    try {
      setIsDeleting(fileName)
      const response = await JobFileApi.deleteFile({ fileName, userType: user.userType }, user.id)
      if (response?.status) {
        openNotificationWithIcon('success', 'Success', response.message)
        mixpanel.identify(user.email)
        mixpanel.track('File delete successfully', { JobId: job.id, userId: user.id, userType: user.userType })
        const updatedFiles = files.filter((f) => f.jobFileData.fileName !== fileName)
        setFiles([...updatedFiles])
        const totalPages = Math.ceil(updatedFiles.length / pageSize)
        if (currentPage > totalPages) setCurrentPage(Math.max(totalPages, 1))
        socket.emit('refresh-file-list', { jobId: job.id, userType: user.userType, job, deleteFile: true })
      } else {
        openNotificationWithIcon('error', 'Error', response?.message)
      }
    } catch (error) {
      console.error('Error deleting file:', error)
      openNotificationWithIcon('error', 'Error', `Failed to delete file ${fileName}.`)
    } finally {
      setIsDeleting(null)
    }
  }

  const handleDownload = async (fileName) => {
    try {
      setIsDownloading(fileName)
      const response = await JobFileApi.downloadFile({ fileName }, user.id)
      if (response?.status) {
        const link = document.createElement('a')
        link.href = response.url
        link.setAttribute('download', fileName)
        window.open(link.href, '_blank')
        openNotificationWithIcon('success', 'Success', response.message)
        mixpanel.identify(user.email)
        mixpanel.track('File download successfully', { JobId: job.id, userId: user.id, userType: user.userType })
      } else {
        openNotificationWithIcon('error', 'Error', response?.message)
      }
    } catch (error) {
      console.error('Error downloading file:', error)
      openNotificationWithIcon('error', 'Error', `Failed to download file ${fileName}.`)
    } finally {
      setIsDownloading(null)
    }
  }

  if (filesToShow.length === 0) {
    return (
      <div className="ft-empty">
        <div className="ft-empty-icon-wrap">
          <FileOutlined className="ft-empty-icon" />
        </div>
        <p className="ft-empty-title">No files uploaded yet</p>
        <p className="ft-empty-hint">Upload a file to get started</p>
      </div>
    )
  }

  return (
    <div className="ft-wrapper">
      <div className="ft-header">
        <span className="ft-header-label">
          {files.length} file{files.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="ft-list">
        {filesToShow.map((file, index) => {
          const { fileName } = file.jobFileData
          const deletable = canDelete(file)
          return (
            <div key={index} className="ft-row">
              <div className="ft-row-icon-wrap">
                <FileIcon name={fileName} />
              </div>
              <div className="ft-row-info">
                <span className="ft-row-name" title={fileName}>{shortName(fileName)}</span>
                <FileTypeBadge name={fileName} />
              </div>
              <div className="ft-row-actions">
                <button
                  className="ft-action-btn ft-action-btn--download"
                  onClick={() => handleDownload(fileName)}
                  title="Download"
                  disabled={isDownloading === fileName}
                >
                  {isDownloading === fileName
                    ? <Spin size="small" />
                    : <><DownloadOutlined /><span className="ft-btn-label">Download</span></>}
                </button>
                {deletable && (
                  <button
                    className="ft-action-btn ft-action-btn--delete"
                    onClick={() => handleDelete(fileName)}
                    title="Delete"
                    disabled={!!isDeleting}
                  >
                    {isDeleting === fileName ? <Spin size="small" /> : <DeleteOutlined />}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {files.length > pageSize && (
        <div className="ft-pagination">
          <Pagination
            current={currentPage}
            total={files.length}
            pageSize={pageSize}
            onChange={setCurrentPage}
            size="small"
          />
        </div>
      )}
    </div>
  )
}

export default FileTable
